import {
  AmountInputField,
  FormDivider,
  Partnership,
  SecondaryApplicantInquiry,
} from '@hex/calculators-shared/components';
import { LTV_THRESHOLD, featureFlags } from '@hex/calculators-shared/constants';
import { useSapphireMedia } from '@hex/calculators-shared/hooks';
import { ReferenceInterestRateResponse } from '@hex/calculators-shared/types';
import { formatMoney } from '@hex/calculators-shared/utils';
import { Home, Sack } from '@sapphire/icons/react';
import { Button, Flex, Icon, tokens, HelpButton, Typography } from '@sapphire-react/core';
import { RefObject, useEffect } from 'react';
import type { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { CostResponse, FormDataType } from '../../types';
import { useCostForm } from './useCostForm.hook';

enum PolarQuestion {
  No = '0',
  Yes = '1',
}

type Props = {
  buttonRef: RefObject<any>;
  responseData: CostResponse | ReferenceInterestRateResponse | null;
  loading?: boolean;
  onFormChange: () => void;
  onSubmit: (data: FormDataType, isDirty: boolean, reset: UseFormReset<FormDataType>) => void;
};

export function Form({ onSubmit, responseData, onFormChange, buttonRef, loading }: Props) {
  const { t } = useTranslation(['costCalculator', 'shared']);
  const { isExtraSmall, isSmall } = useSapphireMedia();
  const inputSize = isExtraSmall || isSmall ? 'md' : 'lg';

  const {
    control,
    ltv,
    isDirty,
    propertyValueFieldRules,
    loanAmountFieldRules,
    formFields,
    handleSubmit,
    trackCalculatorStarted,
    reset,
  } = useCostForm();

  // Resets results
  useEffect(() => {
    if (isDirty && responseData) {
      onFormChange();
    }
  }, [isDirty, onFormChange, responseData]);

  return (
    <S.Form
      onSubmit={handleSubmit((formData) => onSubmit(formData, isDirty, reset))}
      autoComplete={'off'}
      onClick={trackCalculatorStarted}
    >
      <Flex flexDirection={'column'} gap={tokens.size.spacingMd}>
        <AmountInputField
          control={control}
          name={'propertyValue'}
          label={t('shared:personalInformation.inputs.propertyValue.label')}
          width={'100%'}
          rules={propertyValueFieldRules}
          postfix={'kr'}
          max={9_999_999_999}
          prefix={
            <Icon size={inputSize}>
              <Home />
            </Icon>
          }
        />
        <AmountInputField
          control={control}
          name={'loanAmount'}
          label={t('shared:personalInformation.inputs.loanAmount.label')}
          rules={loanAmountFieldRules}
          width={'100%'}
          postfix={'kr'}
          max={9_999_999_999}
          contextualHelp={
            <HelpButton
              variant={'help'}
              aria-label={'Hjälp!'}
              contentBody={
                <>
                  <Typography.Body size={'sm'}>
                    {t('shared:personalInformation.inputs.loanAmount.tooltip._1', { percentage: LTV_THRESHOLD })}
                  </Typography.Body>
                  <Typography.Body marginTop={tokens.size.spacing2xs} size={'sm'}>
                    {t('shared:personalInformation.inputs.loanAmount.tooltip._2', {
                      amount1: formatMoney(1000000),
                      amount2: formatMoney(1000000 * (LTV_THRESHOLD / 100)),
                    })}
                  </Typography.Body>
                </>
              }
            />
          }
          prefix={
            <Icon size={inputSize}>
              <Sack />
            </Icon>
          }
          note={
            <>
              {t('shared:personalInformation.inputs.loanAmount.note')} {ltv !== undefined && `${ltv} %`}
            </>
          }
        />
        <FormDivider />
        <SecondaryApplicantInquiry control={control} />
        {!featureFlags.use3MonthInterestRate && (
          <Partnership
            control={control}
            withSecondaryApplicant={formFields.secondaryApplicantExist === PolarQuestion.Yes}
          />
        )}
      </Flex>
      <Flex justifyContent={'center'} marginTop={tokens.size.spacing2xl}>
        <Button ref={buttonRef} type={'submit'} isDisabled={loading} name={'Beräkna'}>
          {loading ? t('shared:loading') : t('shared:calculate')}
        </Button>
      </Flex>
    </S.Form>
  );
}

const S = {
  Form: styled.form`
    display: flex;
    flex-direction: column;
  `,
};
